import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { logger } from '@/utils/logger/logger';

import {
  PLANNER_BACKGROUND_REFRESH_INTERVAL_MS,
  PLANNER_BACKGROUND_REFRESH_MIN_GAP_MS,
  PLANNER_BACKGROUND_REFRESH_MOUNT_DELAY_MS,
  PLANNER_PAGINATION_LOG_TAG,
} from '../../constants';
import { refetchActiveAssignmentQueriesInBackground } from '../../helpers/assignment-cache-utils';
import usePlannerStore from '../../store';

/**
 * Keeps the planner's assignment data fresh for a long-lived, multi-user session
 * WITHOUT reloading the UI.
 *
 * The planner cache is intentionally kept alive for the whole session
 * (`gcTime: Infinity`) and never auto-refetches (`staleTime: Infinity`) because
 * horizontal pages are merged manually and mutations patch the cache in place.
 * That makes the data stale relative to other users' changes. This hook bridges
 * the gap: on the right triggers it fires the SAME background refresh used after
 * a mutation (`refetchActiveAssignmentQueriesInBackground`), which:
 *
 * - replaces only the active tab's vertical pages, preserving horizontal extensions,
 * - shows NO loading skeleton (fire-and-forget, no `isLoadingDateRange` flip),
 * - leaves filters, the date window, and scroll position untouched.
 *
 * Triggers:
 * - tab regains focus / visibility (you came back from another app/tab),
 * - navigating into the planner (mount), after a short delay,
 * - a periodic interval while the planner is mounted.
 *
 * Safety guards (skip the refresh, never queue it) — refreshing during any of
 * these would clobber optimistic/in-flight state or fight the user:
 * - an assignment drag/resize/cut/duplicate is in progress,
 * - a horizontal date-range fetch is already loading.
 *
 * A minimum-gap throttle prevents a focus event right after the interval (or a
 * remount) from double-fetching.
 */
export function usePlannerBackgroundRefresh(): void {
  const queryClient = useQueryClient();
  const lastRefreshAtRef = useRef<number>(performance.now());

  useEffect(() => {
    const isInteractionInFlight = () => {
      const state = usePlannerStore.getState();
      return (
        state.isDragResizeInteracting ||
        state.activeCutId !== null ||
        state.activeDuplicateId !== null ||
        state.isLoadingDateRange
      );
    };

    const refreshIfIdle = (reason: string) => {
      const now = performance.now();

      if (now - lastRefreshAtRef.current < PLANNER_BACKGROUND_REFRESH_MIN_GAP_MS) {
        logger.debug(
          PLANNER_PAGINATION_LOG_TAG,
          `backgroundRefresh: ${reason} skipped (within min gap)`
        );
        return;
      }

      if (isInteractionInFlight()) {
        logger.debug(
          PLANNER_PAGINATION_LOG_TAG,
          `backgroundRefresh: ${reason} skipped (interaction/fetch in flight)`
        );
        return;
      }

      lastRefreshAtRef.current = now;
      logger.debug(PLANNER_PAGINATION_LOG_TAG, `backgroundRefresh: ${reason} → refreshing`);
      // scopeToViewportRun: refresh only the contiguous run the viewport is in
      // (the ~6–12 months in memory), not the full envelope which can span a
      // pruned multi-year gap after far scrolling.
      refetchActiveAssignmentQueriesInBackground(queryClient, true);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshIfIdle('visibility');
    };
    const handleFocus = () => refreshIfIdle('focus');

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    const mountTimeoutId = window.setTimeout(
      () => refreshIfIdle('mount'),
      PLANNER_BACKGROUND_REFRESH_MOUNT_DELAY_MS
    );
    const intervalId = window.setInterval(
      () => refreshIfIdle('interval'),
      PLANNER_BACKGROUND_REFRESH_INTERVAL_MS
    );

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearTimeout(mountTimeoutId);
      window.clearInterval(intervalId);
    };
  }, [queryClient]);
}
